import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './Legal.css';

function PrivacyPolicy() {
  const navigate = useNavigate();
  return (
    <div className="legal-page">
      <nav className="legal-nav">
        <div className="logo" onClick={() => navigate('/')}>
          <img src="/JobLinkedAI.png" alt="JobLinked AI" />
          JobLinked AI
        </div>
        <button className="legal-back-btn" onClick={() => navigate(-1)}>
          <i className="fas fa-arrow-left"></i> Back
        </button>
      </nav>

      <div className="legal-hero">
        <h1>Privacy Policy</h1>
        <p>How we collect, use, and protect your personal information</p>
      </div>

      <div className="legal-content">
        <span className="legal-effective">Effective Date: 18 April 2025</span>

        <div className="legal-section">
          <h2>1. Introduction</h2>
          <p>JobLinked AI ("we", "us", "our") is committed to protecting your personal information in accordance with the Protection of Personal Information Act 4 of 2013 (POPIA) and other applicable South African privacy legislation.</p>
          <p>This Privacy Policy explains what personal information we collect, why we collect it, how we use it, and your rights regarding your data.</p>
        </div>

        <div className="legal-section">
          <h2>2. Information We Collect</h2>
          <h3>2.1 Information You Provide</h3>
          <ul>
            <li>Account details: full name, email address, password</li>
            <li>Profile information: professional title, location, employment status, skills, work experience, education</li>
            <li>Documents: CV/resume, certificates, ID copies, and other uploaded files</li>
            <li>Payment information: processed securely via Paystack (we do not store card details)</li>
            <li>Messages sent through the Platform</li>
          </ul>
          <h3>2.2 Information Collected Automatically</h3>
          <ul>
            <li>Log data: IP address, browser type, pages visited, timestamps</li>
            <li>Device information: operating system, screen resolution</li>
            <li>Usage data: features used, search queries, application activity</li>
            <li>Cookies and similar tracking technologies (see our Cookie Policy)</li>
          </ul>
        </div>

        <div className="legal-section">
          <h2>3. How We Use Your Information</h2>
          <p>We use your personal information to:</p>
          <ul>
            <li>Create and manage your account</li>
            <li>Match you with relevant job opportunities using AI algorithms</li>
            <li>Facilitate communication between job seekers and recruiters</li>
            <li>Process subscription payments</li>
            <li>Send transactional emails (verification, interview invitations, notifications)</li>
            <li>Improve our AI matching algorithms and Platform features</li>
            <li>Comply with legal obligations</li>
            <li>Detect and prevent fraud or abuse</li>
          </ul>
        </div>

        <div className="legal-section">
          <h2>4. Sharing Your Information</h2>
          <p>We do not sell your personal information. We may share it with:</p>
          <h3>4.1 Recruiters</h3>
          <p>When you apply for a job, your profile, CV, and application documents are shared with the relevant recruiter. You control what you include in your profile and applications.</p>
          <h3>4.2 Service Providers</h3>
          <p>We use trusted third-party providers to operate the Platform, including cloud hosting (Railway, Vercel), email delivery (Resend), and payment processing (Paystack). These providers are contractually bound to protect your data.</p>
          <h3>4.3 Legal Requirements</h3>
          <p>We may disclose your information if required by law, court order, or to protect the rights and safety of our users.</p>
        </div>

        <div className="legal-section">
          <h2>5. Data Retention</h2>
          <p>We retain your personal information for as long as your account is active or as needed to provide services. You may request deletion of your account and associated data at any time. Some data may be retained for legal compliance purposes for up to 5 years after account closure.</p>
        </div>

        <div className="legal-section">
          <h2>6. Security</h2>
          <p>We implement industry-standard security measures including:</p>
          <ul>
            <li>Encrypted data transmission (HTTPS/TLS)</li>
            <li>Secure password hashing</li>
            <li>Access controls and authentication tokens</li>
            <li>Regular security reviews</li>
          </ul>
          <p>No system is 100% secure. In the event of a data breach affecting your rights, we will notify you as required by POPIA.</p>
        </div>

        <div className="legal-section">
          <h2>7. Your Rights Under POPIA</h2>
          <p>As a data subject, you have the right to:</p>
          <ul>
            <li>Access the personal information we hold about you</li>
            <li>Request correction of inaccurate information</li>
            <li>Request deletion of your personal information</li>
            <li>Object to the processing of your information</li>
            <li>Lodge a complaint with the Information Regulator of South Africa</li>
          </ul>
          <p>To exercise these rights, contact us at <a href="mailto:privacy@joblinked.ai" style={{color:'#60a5fa'}}>privacy@joblinked.ai</a>.</p>
        </div>

        <div className="legal-section">
          <h2>8. Children's Privacy</h2>
          <p>The Platform is not intended for users under 18 years of age. We do not knowingly collect personal information from minors. If we become aware that a minor has registered, we will delete their account.</p>
        </div>

        <div className="legal-section">
          <h2>9. International Transfers</h2>
          <p>Your data is primarily stored and processed in South Africa. Where data is transferred internationally (e.g., to cloud service providers), we ensure adequate protections are in place consistent with POPIA requirements.</p>
        </div>

        <div className="legal-section">
          <h2>10. Changes to This Policy</h2>
          <p>We may update this Privacy Policy from time to time. We will notify you of significant changes via email or a prominent notice on the Platform. The effective date at the top of this page will be updated accordingly.</p>
        </div>

        <div className="legal-contact-box">
          <p>Information Officer: JobLinked AI</p>
          <p>Email: <a href="mailto:privacy@joblinked.ai">privacy@joblinked.ai</a></p>
          <p>You may also contact the <a href="https://www.justice.gov.za/inforeg/" target="_blank" rel="noreferrer">Information Regulator of South Africa</a> if you have concerns about how we handle your data.</p>
        </div>
      </div>

      <footer className="legal-footer">
        <p>&copy; 2025 JobLinked AI. All rights reserved. Built with ❤️ in South Africa</p>
        <p style={{ marginTop: '0.5rem' }}>
          <Link to="/terms">Terms &amp; Conditions</Link>
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/cookies">Cookie Policy</Link>
        </p>
      </footer>
    </div>
  );
}

export default PrivacyPolicy;
